import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { AmplitudeFlagKeys } from 'types/amplitudeFlags';
import camelcaseKeys from 'utils/camelCaseKeys';

import { OrganizationContext } from 'components/AdminNew/Companies/CompanyDetails/OrganizationContext';
import useIsExperimentActive from 'components/Amplitude/useIsExperimentActive';
import { TriggerContext } from 'components/Settings/Billing/types';

import CreditCardsAdminApi from '../api/CreditCardsAdminApi';
import CreditCardsProApi from '../api/CreditCardsProApi';
import { CreditCard } from '../types';

const fetchCreditCardsAdmin = (orgId: number, useUUIDs = false): Promise<CreditCard[]> =>
  new CreditCardsAdminApi(useUUIDs)
    .getCreditCards(orgId)
    .then((response: AxiosResponse) => camelcaseKeys(response.data));

const fetchCreditCardsPro = (useUUIDs = false): Promise<CreditCard[]> =>
  new CreditCardsProApi(useUUIDs).getCreditCards().then((response: AxiosResponse) => camelcaseKeys(response.data));

export const useCreditCards = () => {
  const { organizationId, triggerContext: context } = useContext(OrganizationContext);
  const useCreditCardsUUIDs = useIsExperimentActive(AmplitudeFlagKeys.billingEnableCreditCardsUUIDs);

  let queryFn = () => fetchCreditCardsPro(useCreditCardsUUIDs);

  if (context === TriggerContext.AdminPanel) {
    if (!organizationId) {
      throw new Error('No organization id given for fetching credit cards');
    }

    queryFn = () => fetchCreditCardsAdmin(organizationId, useCreditCardsUUIDs);
  }

  return useQuery<CreditCard[]>(['creditCards', organizationId], queryFn, {
    retry: 0,
  });
};

export const usePrimaryCreditCard = () => {
  const { data, isLoading, isError, error, isFetched } = useCreditCards();

  const primaryCreditCard = data?.find((card) => card.primary);

  return {
    data: primaryCreditCard,
    isLoading,
    isError,
    error,
    isFetched,
  };
};
